import {
  AssignmentModel,
  CaseExtendedModel,
  ExpensePredictionResponse,
} from 'api/model';
import Button from 'components/inputs/Button';
import Input from 'components/inputs/Input';
import LabelWrap from 'components/inputs/LabelWrap';
import { FC, useMemo, useState } from 'react';
import styled from 'styled-components';
import { showException } from 'utils/exception-helper';
import { useCaseGetCase } from 'api/case/case';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import { ColumnRenderProps } from 'pages/GlobalSearch/BulkApproveAssignmentsList/useColumnSettings';
import useTranslations from 'contexts/basicData/useTranslations';
import { useLocalStorage } from 'usehooks-ts';
import { useAiPredictExpense } from 'api/ai/ai';
import useUsers from 'contexts/basicData/useUsers';
import MostRecentClosedCases from './MostRecentClosedCases';

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;

  align-items: flex-start;
  gap: 20px;
  padding: 20px;
  textarea {
    width: 500px;
    height: 600px;
  }

  pre {
    max-width: 90vw;
    overflow: auto;
    white-space: pre;
    resize: both;
    border: 1px solid ${({ theme }) => theme.colors.border.primary};
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TestAIInferencePage: FC = () => {
  const { assignmentTypes } = useTranslations();
  const users = useUsers();

  const predicitonCall = useAiPredictExpense();

  const [currentCase, setCurrentCase] = useState<CaseExtendedModel | null>(
    null
  );
  const getCaseCall = useCaseGetCase();

  const [caseId, setCaseId] = useLocalStorage<string>(
    'caseIdaipredicition',
    ''
  );
  const [assignmentPredicitions, setAssignmentPredictions] = useState<
    Record<number, ExpensePredictionResponse | undefined>
  >({});

  const getCase = async (_id: string) => {
    try {
      const caseData = await getCaseCall.mutateAsync({
        params: { caseId: Number(_id) },
      });
      setCurrentCase(caseData);
    } catch (error) {
      showException(error);
    }
  };

  const columnSettings = useMemo((): ColumnSetting<
    AssignmentModel,
    ColumnRenderProps
  >[] => {
    return [
      {
        head: 'ID',
        render: (assignment: AssignmentModel) => (
          <span>{assignment.assignmentID}</span>
        ),
        width: 100,
      },
      {
        head: 'Typ',
        render: (assignment: AssignmentModel) => (
          <span>{assignmentTypes[assignment.assignmentTypeID]}</span>
        ),
        width: 100,
      },
      {
        head: 'expenses',
        render: (assignment: AssignmentModel) => {
          const assignmentExpenses = currentCase?.expenses.filter(
            (x) => x.assignmentID === assignment.assignmentID
          );
          const aggregatedExpenses =
            assignmentExpenses?.reduce(
              (acc, expense) => acc + expense.value,
              0
            ) || 0;
          const formattedExpenses = new Intl.NumberFormat('sv-SE', {
            style: 'currency',
            currency: 'SEK',
          }).format(aggregatedExpenses);
          return <span>{formattedExpenses}</span>;
        },
        width: 100,
      },

      {
        head: 'Prediction',
        render: (assignment: AssignmentModel) => {
          const prediction = assignmentPredicitions[assignment.assignmentID];

          const formattedPrediction = new Intl.NumberFormat('sv-SE', {
            style: 'currency',
            currency: 'SEK',
          }).format(prediction?.predictedExpense || 0);

          if (prediction === undefined) {
            return <span>-</span>;
          }

          if (prediction.errorMessage) {
            return (
              <div
                style={{
                  color: 'red',
                  whiteSpace: 'wrap',
                }}
              >
                {prediction.errorMessage}
              </div>
            );
          }
          // return button to trigger prediction
          return <span>{formattedPrediction}</span>;
        },
        width: 200,
      },
      {
        head: '',
        render: (assignment: AssignmentModel) => {
          // return button to trigger prediction
          return (
            <Button
              onClick={async () => {
                try {
                  let actualDurationMinutes: number = 0; // this should be removed from the training data
                  if (assignment.startTime && assignment.endTime) {
                    const start = new Date(assignment.startTime);
                    const end = new Date(assignment.endTime);
                    actualDurationMinutes =
                      (end.getTime() - start.getTime()) / (1000 * 60);
                  }
                  let estimatedDurationMinutes: number = 0;
                  if (assignment.estimatedDuration) {
                    estimatedDurationMinutes =
                      assignment.estimatedDuration / 60 / 1000;
                  }
                  let mileagePerYear: number = 0;
                  if (
                    currentCase?.case.vehicleMileage &&
                    currentCase?.case.vehicleYearModel
                  ) {
                    const currentYear = new Date().getFullYear();
                    const yearModel = Number(
                      currentCase?.case.vehicleYearModel
                    );
                    const mileage = Number(currentCase?.case.vehicleMileage);
                    mileagePerYear = mileage / (currentYear - yearModel + 1);
                  }
                  if (mileagePerYear < 0) {
                    mileagePerYear = 0;
                  }
                  const ageAtAssignment = 40;
                  const isHoliday = false; // Placeholder, replace with actual logic if needed
                  const user = users[assignment.assignedTo?.userID || 0];
                  const targetDate = assignment.bookedTo;
                  const predictionResult = await predicitonCall.mutateAsync({
                    data: {
                      actualDurationMinutes,
                      ageAtAssignment,
                      assignmentDeviationID: assignment.assignmentDeviationID,
                      canDoHomeDelivery: user.canDoHomeDelivery,
                      canDoLongDrives: user.canDoLongDrives,
                      canDriveCarTransportTrucks:
                        user.canDriveCarTransportTrucks,
                      canDrivePremium: user.canDrivePremium,
                      canDriveTrailers: user.canDriveTrailers,
                      canDriveTrucks: user.canDriveTrucks,
                      canStayAtHotels: user.canStayAtHotels,
                      canTestPremiumCars: user.canTestPremiumCars,
                      canTestTrucks: user.canTestTrucks,
                      caseTypeID: currentCase?.case.caseTypeID || 0,
                      companyID: currentCase?.case.companyID || 0,
                      dayOfWeek: new Date(assignment.startTime || '').getDay(),
                      estimatedDurationMinutes,
                      fromZip2:
                        assignment.fromZip?.trim().substring(0, 2) || '',
                      hasLinkedCases: currentCase?.case.hasLinkedCases || false,
                      hourOfDay: targetDate.getHours(),
                      isElectric: currentCase?.case.isElectric || false,
                      isHoliday,
                      isPremiumCar: currentCase?.case.isPremiumCar || false,
                      isTrailer: currentCase?.case.isTrailer || false,
                      isTruck: currentCase?.case.isTruck || false,
                      mileagePerYear,
                      monthOfYear: targetDate.getMonth() + 1,
                      originalFromZip2:
                        currentCase?.case.originalFromZip
                          ?.trim()
                          .substring(0, 2) || '',
                      postalCode2:
                        user.postalCode?.trim().substring(0, 2) || '',
                      toZip2: assignment.toZip?.trim().substring(0, 2) || '',
                      vehicleBody: currentCase?.case.vehicleBody || '',
                      vehicleBrand: currentCase?.case.vehicleBrand || '',
                      vehicleFuel: currentCase?.case.vehicleFuel || '',
                      vehicleGearBox: currentCase?.case.vehicleGearBox
                        ? '1'
                        : '0',
                      vehicleHorsePower:
                        currentCase?.case.vehicleHorsePower || 0,
                      vehicleMileage: currentCase?.case.vehicleMileage || 0,
                      vehicleMileage_Filled: !!currentCase?.case.vehicleMileage,
                      vehicleModel: currentCase?.case.vehicleModel || '',
                      vehicleType: currentCase?.case.vehicleType || '',
                      vehicleTyreType: currentCase?.case.vehicleTyreType
                        ? '1'
                        : '0',
                      vehicleValuation: currentCase?.case.vehicleValuation || 0,
                      vehicleWeight: currentCase?.case.vehicleWeight || 0,
                      vehicleYearModel: currentCase?.case.vehicleYearModel
                        ? Number(currentCase?.case.vehicleYearModel)
                        : 0,
                      vehicleYearModel_Filled:
                        !!currentCase?.case.vehicleYearModel,
                      isWeekend: [0, 6].includes(targetDate.getDay()),
                    },
                  });
                  setAssignmentPredictions((prev) => ({
                    ...prev,
                    [assignment.assignmentID]: predictionResult,
                  }));
                } catch (error) {
                  showException(error);
                }
              }}
              disabled={predicitonCall.status === 'loading'}
            >
              Predict
            </Button>
          );
        },
        width: 150,
      },
    ];
  }, [
    assignmentPredicitions,
    assignmentTypes,
    currentCase?.case.caseTypeID,
    currentCase?.case.companyID,
    currentCase?.case.hasLinkedCases,
    currentCase?.case.isElectric,
    currentCase?.case.isPremiumCar,
    currentCase?.case.isTrailer,
    currentCase?.case.isTruck,
    currentCase?.case.originalFromZip,
    currentCase?.case.vehicleBody,
    currentCase?.case.vehicleBrand,
    currentCase?.case.vehicleFuel,
    currentCase?.case.vehicleGearBox,
    currentCase?.case.vehicleHorsePower,
    currentCase?.case.vehicleMileage,
    currentCase?.case.vehicleModel,
    currentCase?.case.vehicleType,
    currentCase?.case.vehicleTyreType,
    currentCase?.case.vehicleValuation,
    currentCase?.case.vehicleWeight,
    currentCase?.case.vehicleYearModel,
    currentCase?.expenses,
    predicitonCall,
    users,
  ]);

  return (
    <Wrapper>
      <Column>
        <div>
          <h1>Hämta ett ärende</h1>

          <LabelWrap>
            <Input
              style={{ height: 35 }}
              type="number"
              value={caseId}
              onChange={(eve) => {
                const { value } = eve.currentTarget;
                setCaseId(value);
                if (value) {
                  getCase(value);
                } else {
                  setCurrentCase(null);
                }
              }}
            />
          </LabelWrap>

          <Button
            onClick={() => getCase(caseId)}
            disabled={getCaseCall.status === 'loading' || !caseId}
          >
            Hämta ärende
          </Button>
        </div>
        <MostRecentClosedCases
          onSelect={(id: number) => {
            setCaseId(id.toString());
            getCase(id.toString());
          }}
        />
      </Column>
      <Column>
        {currentCase && (
          <Table
            useColumnWidthAsFlexAmount
            columnSettings={columnSettings}
            rows={currentCase?.case.assignmentList}
          />
        )}
      </Column>
    </Wrapper>
  );
};

export default TestAIInferencePage;
